<template>
  <div ref="root" class="root">
    <div id="main" ref="main" class="Main">
      <component
        :is="investmentComponent"
        v-if="isSupported"
        :api-source="apiSource"
        @loading-progress-to-app="buildingLoadingProgressUpdated"
        @all-colliders-loaded="handleCollidersLoading"
        @loading="updateLoading"
      ></component>
      <NotSupportedBrowser v-else />
      <transition name="fade" mode="in-out">
        <Loading
          v-if="(!isEveryColliderLoaded || isLoading) && isSupported"
          :loading-progress="buildingLoadingProgress"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import { isMobile } from 'mobile-device-detect'

import ApiSource from '@/classes/ApiSource.js'
import Loading from '@/components/Loading'
import NotSupportedBrowser from '@/components/NotSupportedBrowser'
import { handleSamsungBrowser } from '@/utils/samsungBrowser'
import { checkBrowserSupport } from '@/utils/supportedBrowsers'
import { setColors } from '@/utils/setColors'
import { mutations } from '@/utils/store'

export default {
  name: 'App',
  components: {
    Investment: () =>
      import(/* webpackChunkName: 'i-chunk' */ '@/components/InvestmentComponent.vue'),
    Loading,
    NotSupportedBrowser,
  },
  data() {
    return {
      isEveryColliderLoaded: true,
      loading: 1,
      buildingLoadingProgress: '',
      investmentComponent: '',
      isSupported: false,
      apiSource: null,
    }
  },
  computed: {
    isLoading() {
      return this.loading > 0
    },
  },
  watch: {
    apiSource() {
      this.updatePageTitle()
    },
  },
  created() {
    window.addEventListener('resize', this.setMainContainerDims)
    window.addEventListener('touchmove', this.handleTouch, { passive: false })
    this.handleUnsupportedBrowsers()
    this.apiSource = new ApiSource()
    mutations.setAssetsEndpoint(this.apiSource.meta.endpoint)
    handleSamsungBrowser()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setMainContainerDims)
    window.removeEventListener('touchmove', this.handleTouch)
  },
  mounted() {
    this.setMainContainerDims()
    this.investmentComponent = 'Investment'
    setColors(this.apiSource.getCSSVariables())
  },
  methods: {
    handleCollidersLoading(value) {
      this.isEveryColliderLoaded = value
    },
    getWindowAspectRatio() {
      let windowAspectRatio = window.innerWidth / window.innerHeight

      return windowAspectRatio.toFixed(2)
    },
    isLandscape() {
      return window.innerHeight > window.innerWidth
    },
    setMainContainerDims() {
      let mainContainer = this.$refs.main
      let root = this.$refs.root
      let screenHeight = window.innerHeight
      let screenWidth = window.innerWidth
      let screenHeightWithNoBottomBar = screenHeight
      let mainContainerHeight
      let mainContainerWidth
      // const windowAspectRatio = Number(this.getWindowAspectRatio())
      const isLandscape = this.isLandscape()

      if (isMobile && isLandscape) {
        screenHeightWithNoBottomBar = screenHeight - 50

        mainContainerWidth = Math.floor(screenHeightWithNoBottomBar * 1.78)
        let mainContainerWidthAsString = `${mainContainerWidth}`
        mainContainerWidthAsString =
          mainContainerWidthAsString.substr(0, mainContainerWidthAsString.length - 1) + `0`
        mainContainerWidth = Number(mainContainerWidthAsString)

        mainContainerHeight = screenHeightWithNoBottomBar
      } else {
        if (screenWidth > 1920) {
          screenWidth = 1920
        }
        mainContainerWidth = screenWidth
        mainContainerHeight = Math.floor(screenWidth / 1.77)
        let mainContainerHeightAsString = `${mainContainerHeight}`
        mainContainerHeightAsString =
          mainContainerHeightAsString.substr(0, mainContainerHeightAsString.length - 1) + `0`
        mainContainerHeight = Number(mainContainerHeightAsString)

        if (mainContainerHeight > screenHeight) {
          mainContainerWidth = Math.floor(screenHeight * 1.78)
          let mainContainerWidthAsString = `${mainContainerWidth}`
          mainContainerWidthAsString =
            mainContainerWidthAsString.substr(0, mainContainerWidthAsString.length - 1) + `0`
          mainContainerWidth = Number(mainContainerWidthAsString)
          mainContainerHeight = screenHeight
        }
      }

      if (mainContainerWidth == 0 || mainContainerHeight == 0) {
        setTimeout(() => {
          this.setMainContainerDims()
        }, 50)
      }
      root.style.width = `${mainContainerWidth}px`
      root.style.height = `${mainContainerHeight}px`

      mainContainer.style.height = `${mainContainerHeight}px`
      mainContainer.style.width = `${mainContainerWidth}px`
    },
    buildingLoadingProgressUpdated(buildingLoadingProgress) {
      this.buildingLoadingProgress = buildingLoadingProgress
    },
    handleTouch(event) {
      if (event.touches.length > 1) {
        event.preventDefault()
      }
    },
    updatePageTitle() {
      const title = this.apiSource.getInvestmentMetaTitle()
      if (title) {
        document.title = title
      }
    },
    updateLoading(value) {
      this.loading += value
      this.buildingLoadingProgress = ''
    },
    handleUnsupportedBrowsers() {
      this.isSupported = checkBrowserSupport()

      if (!this.isSupported) {
        const alertContainer = document.querySelector('.critical-alert-container')
        if (alertContainer) {
          alertContainer.classList.add('critical-alert-container--not-supported')
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@/styles/reset.scss';
@import '~@/styles/Main.scss';
</style>
